import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import MetricsGrid from './MetricsGrid'
import CommutersMetrics from './CommutersMetrics'
import {
  DashboardPeriod,
  DashboardProgramStats,
  DashboardProgramStatsByPeriod,
} from '../dashboardTypes'
import { ChangeVariant } from './TitleChange'

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  7: 'previous 7d',
  30: 'previous 30d',
  90: 'previous 90d',
  all: 'all time',
}

const percentChange = (current: number, previous: number) => {
  if (current === 0 && previous === 0) {
    return 0
  }
  if (previous === 0) {
    return current === 0 ? 0 : 100
  }
  return Math.round(((current - previous) / previous) * 100)
}

const changeVariant = (value: number): ChangeVariant => {
  if (value > 0) {
    return 'positive'
  }
  if (value < 0) {
    return 'negative'
  }
  return 'neutral'
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const formatMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

interface ProgramsStatsGridProps {
  statsByPeriod?: DashboardProgramStatsByPeriod | null
  isLoading?: boolean
  period: DashboardPeriod
  showTitle?: boolean
  moneyLabel?: string
  activeLabel?: string
  pendingLabel?: string
  showPending?: boolean
}

const ProgramsStatsGrid = ({
  statsByPeriod = null,
  isLoading = false,
  period,
  showTitle = true,
  moneyLabel = 'Money Spent',
  activeLabel = 'Active Cards',
  pendingLabel = 'Pending Activation',
  showPending = true,
}: ProgramsStatsGridProps) => {
  const stats: DashboardProgramStats | undefined = statsByPeriod?.[period]
  const periodLabel = PERIOD_LABELS[period]
  const showLoading = isLoading || !stats

  const enrolledChange = percentChange(
    stats?.enrolledEmployees.current ?? 0,
    stats?.enrolledEmployees.previous ?? 0
  )
  const activeCardsChange = percentChange(
    stats?.activeCards.current ?? 0,
    stats?.activeCards.previous ?? 0
  )
  const pendingChange = percentChange(
    stats?.pendingActivation.current ?? 0,
    stats?.pendingActivation.previous ?? 0
  )
  const spendChange = percentChange(
    stats?.moneySpentCents.current ?? 0,
    stats?.moneySpentCents.previous ?? 0
  )

  return (
    <Section data-cy='programs-stats-grid'>
      {showTitle ? (
        <Header>
          <Text variant='h3' fontWeight={500}>
            Programs
          </Text>
        </Header>
      ) : null}
      <MetricsGrid
        spacing={2}
        noOfColumns={showPending ? 4 : 3}
        columns={[
          <div data-cy='programs-stat-enrolled'>
            <CommutersMetrics
              title={formatCount(stats?.enrolledEmployees.current ?? 0)}
              titleFontWeight={600}
              titleVariant='h2'
              subtitle='Enrolled Employees'
              change={
                period === 'all'
                  ? undefined
                  : `${enrolledChange}% from ${periodLabel}`
              }
              isLoading={showLoading}
              variant={changeVariant(enrolledChange)}
            />
          </div>,
          <div data-cy='programs-stat-active-cards'>
            <CommutersMetrics
              title={formatCount(stats?.activeCards.current ?? 0)}
              titleFontWeight={600}
              titleVariant='h2'
              subtitle={activeLabel}
              change={
                period === 'all'
                  ? undefined
                  : `${activeCardsChange}% from ${periodLabel}`
              }
              isLoading={showLoading}
              variant={changeVariant(activeCardsChange)}
            />
          </div>,
          showPending ? (
            <div data-cy='programs-stat-pending'>
              <CommutersMetrics
                title={formatCount(stats?.pendingActivation.current ?? 0)}
                titleFontWeight={600}
                titleVariant='h2'
                subtitle={pendingLabel}
                change={
                  period === 'all'
                    ? undefined
                    : `${pendingChange}% from ${periodLabel}`
                }
                isLoading={showLoading}
                variant={changeVariant(pendingChange)}
              />
            </div>
          ) : null,
          <div data-cy='programs-stat-money-spent'>
            <CommutersMetrics
              title={formatMoney(stats?.moneySpentCents.current ?? 0)}
              titleFontWeight={600}
              titleVariant='h2'
              subtitle={moneyLabel}
              change={
                period === 'all'
                  ? undefined
                  : `${spendChange}% from ${periodLabel}`
              }
              isLoading={showLoading}
              variant={changeVariant(spendChange)}
            />
          </div>,
        ].filter(Boolean)}
      />
    </Section>
  )
}

export default React.memo(ProgramsStatsGrid)
